import { createContext, useContext } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Create a client with default cache configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache analytics data for 5 minutes
      staleTime: 5 * 60 * 1000, // Data considered fresh for 5 minutes
      cacheTime: 10 * 60 * 1000, // Keep unused data in cache for 10 minutes
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      refetchOnReconnect: true, // Refetch when network reconnects
      retry: 1, // Retry failed requests once
      retryDelay: 1000, // Wait 1 second before retrying
    },
  },
});

// Context for accessing query client directly if needed
const DataCacheContext = createContext(queryClient);

export function DataCacheProvider({ children }) {
  return (
    <QueryClientProvider client={queryClient}>
      <DataCacheContext.Provider value={queryClient}>
        {children}
      </DataCacheContext.Provider>
    </QueryClientProvider>
  );
}

export function useQueryClient() {
  return useContext(DataCacheContext);
}

// Helper function to invalidate specific cache keys
export function invalidateCache(keys) {
  if (Array.isArray(keys)) {
    keys.forEach(key => queryClient.invalidateQueries({ queryKey: [key] }));
  } else {
    queryClient.invalidateQueries({ queryKey: [keys] });
  }
}

// Helper function to clear all caches
export function clearAllCaches() {
  queryClient.clear();
}

export default queryClient;
